import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle, Loader, AlertCircle } from 'react-feather';
import './UpgradeSubscription.css';
import { API_BASE_URL } from '../../config/api';

const ENTERPRISE_URL = 'https://nexopta.com/enterprise-solutions/';

// Light-theme segmented Toggle (no inline styles)
const Toggle = ({ value, onChange }) => (
  <div className="upgrade-toggle-wrap">
    <div className="upgrade-toggle" role="group" aria-label="Billing cycle">
      <button
        type="button"
        onClick={() => onChange('monthly')}
        className={`upgrade-toggle-btn ${value === 'monthly' ? 'is-active' : ''}`}
        aria-pressed={value === 'monthly'}
      >
        Monthly
      </button>

      <button
        type="button"
        onClick={() => onChange('yearly')}
        className={`upgrade-toggle-btn ${value === 'yearly' ? 'is-active' : ''}`}
        aria-pressed={value === 'yearly'}
      >
        Yearly
      </button>
    </div>
  </div>
);

const UpgradeSubscription = ({ currentPlanType, onClose, onUpdateSuccess }) => {
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(currentPlanType || 'lite');
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Keep this stable (avoid re-creating object every render)
  const planDetails = useMemo(
    () => ({
      lite: {
        name: 'Lite',
        description: 'For small businesses ready to explore AI without overcommitting.',
        prices: {
          monthly: parseFloat(process.env.REACT_APP_LITE_MONTHLY_PRICE || 97),
          yearly: parseFloat(process.env.REACT_APP_LITE_ANNUAL_PRICE || 970),
        },
        features: ['1 Operational AI Agents', 'Basic Analytics', 'Email Support'],
        plan_id: {
          monthly: parseInt(process.env.REACT_APP_LITE_MONTHLY_ID),
          yearly: parseInt(process.env.REACT_APP_LITE_ANNUAL_ID),
        },
      },
      business: {
        name: 'Foundation',
        description: 'For businesses ready to explore AI without overcommitting.',
        prices: {
          monthly: parseFloat(process.env.REACT_APP_BUSINESS_PRICE || 347),
        },
        features: [
          '4 Operational AI Agents',
          '7 Critical Integrations',
          'Business Knowledge Base',
          '500 Message Credits',
        ],
        plan_id: {
          monthly: parseInt(process.env.REACT_APP_BUSINESS_ID),
        },
      },
      basic: {
        name: 'Basic',
        description: 'For busy teams that want to automate more and scale faster.',
        prices: {
          monthly: parseFloat(process.env.REACT_APP_BASIC_MONTHLY_PRICE || 197),
          yearly: parseFloat(process.env.REACT_APP_BASIC_ANNUAL_PRICE || 1997),
        },
        features: ['3 Operational AI Agents', 'Advanced Analytics', 'Priority Support'],
        plan_id: {
          monthly: parseInt(process.env.REACT_APP_BASIC_MONTHLY_ID),
          yearly: parseInt(process.env.REACT_APP_BASIC_ANNUAL_ID),
        },
      },
      pro: {
        name: 'Pro',
        description: 'For growing SMBs ready to fully integrate AI into operations.',
        prices: {
          monthly: parseFloat(process.env.REACT_APP_PRO_MONTHLY_PRICE || 297),
          yearly: parseFloat(process.env.REACT_APP_PRO_ANNUAL_PRICE || 2997),
        },
        features: ['5 Operational AI Agents', 'Custom Integrations', 'Dedicated Success Manager'],
        plan_id: {
          monthly: parseInt(process.env.REACT_APP_PRO_MONTHLY_ID),
          yearly: parseInt(process.env.REACT_APP_PRO_ANNUAL_ID),
        },
      },

      // ✅ Enterprise: always visible, clicking takes user to ENTERPRISE_URL
      enterprise: {
        name: 'Enterprise',
        description: 'Custom solutions for larger teams with advanced security, compliance, and support.',
        isExternal: true,
        ctaUrl: ENTERPRISE_URL,
        features: [
            'Unlimited AI Agents',
            'Enterprise Knowledge Base',
            'Dedicated Account Manager',
            'Custom Integrations',
            'Advanced Security',
            'Custom SLA'
        ],
      },
    }),
    []
  );

  const visiblePlans = useMemo(() => {
    return Object.entries(planDetails).filter(([_, plan]) => {
      // Always show Enterprise (no plan_id / no price)
      if (plan?.isExternal) return true;

      const planId = plan?.plan_id?.[selectedBillingCycle];
      const price = plan?.prices?.[selectedBillingCycle];
      return Number.isFinite(planId) && typeof price === 'number';
    });
  }, [planDetails, selectedBillingCycle]);

  useEffect(() => {
    const visibleKeys = visiblePlans.map(([k]) => k);
    if (!visibleKeys.includes(selectedPlan)) {
      const currentKey = currentPlanType?.toLowerCase();
      const fallback = visibleKeys.find((k) => k !== currentKey) || visibleKeys[0];
      if (fallback) setSelectedPlan(fallback);
    }
  }, [selectedBillingCycle, visiblePlans, selectedPlan, currentPlanType]);

  const handleUpdate = async () => {
    setIsProcessing(true);
    setErrorMessage('');

    try {
      // Safety: if something ever selects enterprise, just redirect
      if (selectedPlan === 'enterprise') {
        window.location.assign(ENTERPRISE_URL);
        return;
      }

      const token = localStorage.getItem('access_token');

      if (!token) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        onUpdateSuccess?.({ plan: selectedPlan });
        onClose?.();
        return;
      }

      const plan = planDetails[selectedPlan];
      const payload = {
        event_data: {
          event_type: 'UPDATE_SUBSCRIPTION',
          product: {
            price: plan.prices[selectedBillingCycle],
            interval: selectedBillingCycle,
            trial: false,
            plan_id: plan.plan_id[selectedBillingCycle],
          },
        },
        plan_type: selectedPlan,
      };

      const response = await fetch(`${API_BASE_URL}/api/payment/subscription/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        let errorMsg = 'Failed to update subscription';
        if (data?.error?.message) errorMsg = data.error.message;
        else if (data?.detail) {
          if (typeof data.detail === 'string') errorMsg = data.detail;
          else if (Array.isArray(data.detail)) {
            errorMsg = data.detail.map((err) => err.msg || JSON.stringify(err)).join(', ');
          } else errorMsg = JSON.stringify(data.detail);
        } else if (data?.message) errorMsg = data.message;

        throw new Error(errorMsg);
      }

      onUpdateSuccess?.(data);
      onClose?.();
    } catch (error) {
      setErrorMessage(error?.message || 'Update failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderPrice = (plan) => {
    if (plan?.isExternal) {
      return (
        <div className="card-price">
        </div>
      );
    }

    const price = plan.prices[selectedBillingCycle];
    return (
      <div className="card-price">
        <span className="symbol">$</span>
        <span className="amount">{price}</span>
        <span className="period">/{selectedBillingCycle === 'monthly' ? 'mo' : 'yr'}</span>
      </div>
    );
  };

  return (
    <div className="upgrade-container">
      {/* No heading here, the Modal chrome already titles this dialog
          "Change Subscription Plan". */}
      <div className="upgrade-header">
        <p>
          Your new billing cycle will start immediately. Any remaining time on your current cycle will not be prorated.
        </p>
      </div>

      <div className="upgrade-body">
        <Toggle value={selectedBillingCycle} onChange={setSelectedBillingCycle} />

        {errorMessage && (
          <div className="upgrade-error">
            <AlertCircle size={16} />
            <span>{errorMessage}</span>
          </div>
        )}

        <div className="upgrade-plans-grid">
          {visiblePlans.length === 0 ? (
            <div className="upgrade-empty">No plans available for {selectedBillingCycle} billing.</div>
          ) : (
            visiblePlans.map(([planKey, plan]) => {
              const isCurrent = currentPlanType?.toLowerCase() === planKey;
              const isSelected = selectedPlan === planKey;
              const isExternal = !!plan?.isExternal && !!plan?.ctaUrl;

              const onCardActivate = () => {
                if (isExternal) {
                  window.location.assign(plan.ctaUrl);
                  return;
                }
                if (!isCurrent) setSelectedPlan(planKey);
              };

              return (
                <div
                  key={planKey}
                  className={`upgrade-card ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''}`}
                  onClick={onCardActivate}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') onCardActivate();
                  }}
                >
                  <div className="card-header">
                    <h4>{plan.name}</h4>
                    {isCurrent && <span className="current-badge">Current</span>}
                  </div>

                  {renderPrice(plan)}

                  <p className="card-desc">{plan.description}</p>

                  <ul className="card-features">
                    {plan.features.map((f, i) => (
                      <li key={i}>
                        <CheckCircle size={14} /> {f}
                      </li>
                    ))}
                  </ul>

                  {isExternal && <div className="card-external-cta">Learn more →</div>}
                </div>
              );
            })
          )}
        </div>

        <div className="upgrade-actions">
          <button className="btn-cancel" onClick={onClose} disabled={isProcessing}>
            Cancel
          </button>

          <button
            className="btn-confirm"
            onClick={handleUpdate}
            disabled={
              isProcessing ||
              selectedPlan === currentPlanType?.toLowerCase() ||
              selectedPlan === 'enterprise'
            }
            title={selectedPlan === 'enterprise' ? 'Enterprise is handled via the Enterprise page' : undefined}
          >
            {isProcessing ? (
              <>
                Processing <Loader className="spin" size={14} />
              </>
            ) : selectedPlan === currentPlanType?.toLowerCase() ? (
              'Current Plan'
            ) : selectedPlan === 'enterprise' ? (
              'Visit Enterprise'
            ) : (
              'Confirm Change'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeSubscription;
